import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { UnitOfMeasure } from '@/types/unitOfMeasure';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';

// Mock data - replace with API call
const mockUnitOfMeasure: UnitOfMeasure = {
  id: '1',
  code: 'EA',
  name: 'Each',
  description: 'Individual unit',
  isActive: true,
  createdAt: '2023-01-15T10:30:00Z',
  updatedAt: '2023-01-15T10:30:00Z',
};

const UnitOfMeasureView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unitOfMeasure, setUnitOfMeasure] = useState<UnitOfMeasure | null>(null);

  // Load unit of measure data
  useEffect(() => {
    const fetchUnitOfMeasure = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // In a real app, you would fetch the unit of measure by ID
        const fetchedUnitOfMeasure = mockUnitOfMeasure; // Replace with API call
        
        setUnitOfMeasure(fetchedUnitOfMeasure);
        
      } catch (error) {
        console.error('Error fetching unit of measure:', error);
        // Handle error (e.g., show error message, redirect, etc.)
      } finally {
        setLoading(false);
      }
    };

    fetchUnitOfMeasure();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }


  if (!unitOfMeasure) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="h6">Unit of measure not found</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/settings/unit-of-measures')}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          Unit of Measure Details
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/settings/unit-of-measures')}
            sx={{ mr: 1 }}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/settings/unit-of-measures/${id}/edit`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Code
            </Typography>
            <Typography variant="body1" gutterBottom>
              {unitOfMeasure.code}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Name
            </Typography>
            <Typography variant="body1" gutterBottom>
              {unitOfMeasure.name}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" gutterBottom>
              {unitOfMeasure.description || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Status
            </Typography>
            <Box>
              <Chip
                label={unitOfMeasure.isActive ? 'Active' : 'Inactive'}
                color={unitOfMeasure.isActive ? 'success' : 'default'}
                icon={unitOfMeasure.isActive ? <CheckCircleIcon /> : <CancelIcon />}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Created At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {unitOfMeasure.createdAt ? new Date(unitOfMeasure.createdAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Last Updated
            </Typography>
            <Typography variant="body1" gutterBottom>
              {unitOfMeasure.updatedAt ? new Date(unitOfMeasure.updatedAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UnitOfMeasureView;
